import _extends from "@babel/runtime/helpers/extends";
import React, { useState } from "react";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import { Box, Button, Dropdown, Label, InputText, Grid } from "~/components-shared";
import Calendar from "react-calendar";
import { InputDateRangePeriodEnum } from "~/types/enums";
import Icon from "../Icon";
const Wrapper = styled.div`
  ${_ref => {
  let {
    $s,
    theme
  } = _ref;
  return theme.spacing.resolve(theme, $s);
}};
  ${_ref2 => {
  let {
    $fullWidth
  } = _ref2;
  return $fullWidth && "width: 100%";
}};
  ${_ref3 => {
  let {
    $maxWidth
  } = _ref3;
  return $maxWidth && `max-width: ${$maxWidth}`;
}};
`;
const StyledCalendar = styled(Calendar)`
  padding: 1em;

  .react-calendar__navigation {
    display: flex;
    width: 100%;
    height: 35px;
  }
  .react-calendar__navigation__arrow {
    width: 30px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.1);
    }
  }
  .react-calendar__navigation__arrow,
  .react-calendar__navigation__label {
    background: transparent;
    border: none;
  }

  .react-calendar__month-view__weekdays__weekday {
    text-align: center;

    & > abbr {
      text-decoration: none;
      color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.text.secondary;
}};
    }
  }

  .react-calendar__tile {
    background: transparent;
    border: none;
    height: 40px;
    position: relative;
    z-index: 1;
    cursor: pointer;

    &:after {
      content: "";
      position: absolute;
      z-index: -1;
      top: 50%;
      left: 50%;
      width: 80%;
      padding-top: 80%;
      background-color: transparent;
      border-radius: 50%;
      transform: translate(-50%, -50%);
    }
    
    &:hover:after {
      background-color: rgba(0, 0, 0, 0.1);
    }
    
    &--active {
      font-weight: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.typography.fontWeight.semiBold;
}};
      color: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.colors.background.primary;
}};
      
      &:after,
      &:hover:after {
        background-color: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.colors.primary.main;
}};
      }
  }
  
`;
const InputDateRange = _ref8 => {
  let {
    label,
    initialStartDate = moment().subtract(1, "week").toISOString(),
    initialEndDate = moment().toISOString(),
    direction = "bottomRight",
    error,
    onChange,
    required = false,
    disabled = false,
    definedPeriods = [InputDateRangePeriodEnum.LAST_DAY, InputDateRangePeriodEnum.LAST_THREE_DAYS, InputDateRangePeriodEnum.CURRENT_WEEK, InputDateRangePeriodEnum.LAST_WEEK, InputDateRangePeriodEnum.LAST_TWO_WEEKS, InputDateRangePeriodEnum.CURRENT_MONTH, InputDateRangePeriodEnum.LAST_MONTH, InputDateRangePeriodEnum.LAST_THREE_MONTHS, InputDateRangePeriodEnum.LAST_YEAR],
    color = "translucent",
    maxWidth,
    fullWidth = false,
    s,
    outline,
    children,
    labelProps,
    ...rest
  } = _ref8;
  const theme = useTheme();
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const now = moment().toISOString();
  const predefinedOptions = [{
    period: InputDateRangePeriodEnum.LAST_DAY,
    label: "1 Day",
    start: moment(now).subtract(1, "day").toISOString(),
    end: moment(now).toISOString()
  }, {
    period: InputDateRangePeriodEnum.LAST_THREE_DAYS,
    label: "3 Days",
    start: moment(now).subtract(3, "days").toISOString(),
    end: moment(now).toISOString()
  }, {
    period: InputDateRangePeriodEnum.CURRENT_WEEK,
    label: "Current Week",
    start: moment(now).startOf("week").startOf("day").toISOString(),
    end: moment(now).endOf("week").endOf("day").toISOString()
  }, {
    period: InputDateRangePeriodEnum.LAST_WEEK,
    label: "7 Days",
    start: moment(now).subtract(7, "days").toISOString(),
    end: moment(now).toISOString()
  }, {
    period: InputDateRangePeriodEnum.LAST_TWO_WEEKS,
    label: "14 Days",
    start: moment(now).subtract(14, "days").toISOString(),
    end: moment(now).toISOString()
  }, {
    period: InputDateRangePeriodEnum.CURRENT_MONTH,
    label: "Current Month",
    start: moment(now).startOf("month").startOf("day").toISOString(),
    end: moment(now).endOf("month").endOf("day").toISOString()
  }, {
    period: InputDateRangePeriodEnum.LAST_MONTH,
    label: "1 Month",
    start: moment(now).subtract(1, "month").toISOString(),
    end: moment(now).toISOString()
  }, {
    period: InputDateRangePeriodEnum.LAST_THREE_MONTHS,
    label: "3 Months",
    start: moment(now).subtract(3, "months").toISOString(),
    end: moment(now).toISOString()
  }, {
    period: InputDateRangePeriodEnum.LAST_YEAR,
    label: "1 Year",
    start: moment(now).subtract(1, "year").toISOString(),
    end: moment(now).toISOString()
  }];
  const handleOptionClick = option => {
    setStartDate(option.start);
    setEndDate(option.end);
  };
  const handleStartDateClick = value => {
    setStartDate(moment(value).startOf("day").toISOString());
  };
  const handleEndDateClick = value => {
    setEndDate(moment(value).endOf("day").toISOString());
  };
  const handleApply = () => {
    if (typeof onChange === "function") {
      onChange(startDate, endDate);
    }
  };
  const getLabel = () => {
    if (initialStartDate && initialEndDate) {
      return `${moment(initialStartDate).format("MMM DD, YYYY")} - ${moment(initialEndDate).format("MMM DD, YYYY")}`;
    }
    return "Select date range";
  };
  return /*#__PURE__*/React.createElement(Wrapper, {
    $s: s,
    $maxWidth: maxWidth,
    $fullWidth: fullWidth
  }, /*#__PURE__*/React.createElement(Dropdown, {
    direction: direction,
    customHandler: (toggle, isOpen) => /*#__PURE__*/React.createElement(InputText, _extends({
      label: label,
      value: getLabel(),
      onClick: toggle,
      outline: isOpen || outline,
      autoComplete: "off",
      placeholder: "Select date range",
      elemBefore: /*#__PURE__*/React.createElement(Icon, {
        src: "dateRange",
        scale: 5,
        color: theme.colors.text.secondary,
        s: {
          mr: 2
        }
      }),
      elemAfter: /*#__PURE__*/React.createElement(Icon, {
        src: "chevron",
        scale: 5,
        color: theme.colors.text.secondary,
        rotate: isOpen ? 0 : 180
      }),
      fullWidth: true
    }, rest))
  }, _ref9 => {
    let {
      isOpen,
      onItemClick
    } = _ref9;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen,
      width: "600px",
      maxHeight: "500px"
    }, /*#__PURE__*/React.createElement(Box, {
      s: {
        p: 3
      },
      direction: "column",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      equal: true,
      gap: 3,
      fullWidth: true,
      s: {
        mb: 3
      }
    }, /*#__PURE__*/React.createElement(StyledCalendar, {
      defaultValue: moment(startDate).toDate(),
      value: moment(startDate).toDate(),
      onChange: handleStartDateClick
    }), /*#__PURE__*/React.createElement(StyledCalendar, {
      defaultValue: moment(endDate).toDate(),
      value: moment(endDate).toDate(),
      onChange: handleEndDateClick
    })), /*#__PURE__*/React.createElement(Label, {
      s: {
        mb: 2
      }
    }, "Predefined Options"), /*#__PURE__*/React.createElement(Box, {
      gap: 2,
      s: {
        mb: 6
      }
    }, predefinedOptions.filter(option => definedPeriods.includes(option.period)).map(option => /*#__PURE__*/React.createElement(Button, {
      color: option.start === startDate && option.end === endDate ? "primary" : "grey",
      fontSize: 3,
      s: {
        px: 2,
        py: 1
      },
      key: option.label,
      outline: false,
      onClick: () => handleOptionClick(option)
    }, option.label))), /*#__PURE__*/React.createElement(Box, {
      gap: 3,
      justify: "end",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Button, {
      color: "grey",
      s: {
        px: 6,
        py: 2
      },
      fontSize: 3.5,
      onClick: onItemClick
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      s: {
        px: 6,
        py: 2
      },
      fontSize: 3.5,
      color: "primary",
      onClick: () => {
        handleApply();
        onItemClick();
      }
    }, "Apply"))));
  }));
};
export default InputDateRange;